import CloseIcon from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import {
  QueryFilters,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { API } from 'aws-amplify';
import { GHLSearchData } from '../../../types';
import { getErrorMessage } from '../../../utils/error-message';

export function GHLSubscribe(props: {
  queryKey: QueryFilters['queryKey'];
  active: boolean;
  onClose: () => void;
}) {
  // Hooks
  const queryClient = useQueryClient();

  // Mutation - If exist in Go High Level, subscribe or unsubscribe
  const mutation = useMutation({
    mutationFn: async () => {
      await API.post('ContractingAPI', '/go-high-level/subscribe', {
        body: {
          MrktStatus: props.active ? 'Unsubscribe' : 'Subscribe',
        },
      });
    },
    onSuccess: async () => {
      // Update /search for account in Go High Level
      if (props.queryKey) {
        queryClient.setQueryData<GHLSearchData>(props.queryKey, (oldData) => {
          if (oldData) {
            return {
              ...oldData,
              tags: props.active ? ['unsubscribed'] : ['active', 'email_blast'],
            };
          } else {
            return {
              id: '',
              firstName: '',
              lastName: '',
              source: '',
              phone: '',
              email: '',
              country: '',
              state: '',
              postalCode: '',
              city: '',
              tags: props.active ? ['unsubscribed'] : ['active', 'email_blast'],
            };
          }
        });
      }

      // Close form
      props.onClose();
    },
  });

  const handleClose = () => {
    if (!mutation.isLoading) {
      props.onClose();
    }
  };

  let buttonText = 'Subscribe';
  if (props.active && mutation.isLoading) {
    buttonText = 'Unsubscribing...';
  } else if (mutation.isLoading) {
    buttonText = 'Subscribing...';
  } else if (props.active) {
    buttonText = 'Unsubscribe';
  }

  return (
    <Dialog open onClose={handleClose}>
      <DialogTitle>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>Subscription Status</Box>

          <IconButton
            disabled={mutation.isLoading}
            size="small"
            aria-label="close"
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Change the Subscription Status for your Go High Level account.
        </DialogContentText>
      </DialogContent>

      {mutation.isError ? (
        <Alert
          onClose={() => mutation.reset()}
          severity="error"
          sx={{ borderRadius: 0 }}
        >
          <strong style={{ marginRight: 10 }}>An error has occurred!</strong>
          <small>
            {getErrorMessage({
              error: mutation.error,
              fallback: 'Please try again.',
            })}
          </small>
        </Alert>
      ) : null}

      {mutation.isLoading ? <LinearProgress /> : null}

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          disabled={mutation.isLoading}
          onClick={() => {
            mutation.mutate();
          }}
        >
          {buttonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
